import { tr, usePartnerTranslation } from "../lib/language";
import React from "react";
import { ChevronDown } from "lucide-react";

const FAQ_TOPICS = [
  {
    id: "getting-started",
    get label() { return tr("Getting started as a Voocly partner"); },
    questions: [
      {
        get question() { return tr("What is Voocly?"); },
        get answer() { return tr("Voocly is an online marketplace where travelers and experience lovers discover and book curated tours, activities, and experiences hosted by local partners."); },
      },
      {
        get question() { return tr("Who can become a Voocly partner?"); },
        get answer() { return tr("Any business or qualified individual offering safe and high-quality in-destination experiences can apply to join."); },
      },
      {
        get question() { return tr("How do I sign up as a partner?"); },
        get answer() { return  (
          <>{tr("You can sign up as a partner by completing the online partner application form")}{" "}
            <a
              href="https://www.voocly.com/become-a-partner"
              className="font-semibold text-[#FF5757] underline-offset-4 hover:underline"
            >{tr("here")}</a>
            .
          </>
        ); },
      },
      {
        get question() { return tr("Is there a fee to join Voocly as a partner?"); },
        get answer() { return tr("There is no setup or monthly subscription fee; Voocly works on a pay-per-booking commission model."); },
      },
      {
        get question() { return tr("Can I work with Voocly if I already sell on other OTAs?"); },
        get answer() { return tr("Yes, you can list on Voocly alongside other channels."); },
      },
      {
        get question() { return tr("Can I add multiple team members to my Voocly account?"); },
        get answer() { return tr("Yes, you can create separate user logins for staff with different roles and permissions, such as admin, operations, and finance."); },
      },
    ],
  },
  {
    id: "creating-managing-experiences",
    get label() { return tr("Creating and managing experiences"); },
    questions: [
      {
        get question() { return tr("What types of experiences can I list on Voocly?"); },
        get answer() { return tr("You can list tours, activities, tickets, workshops, rentals, and other in-destination experiences that meet our quality, safety, and legal standards."); },
      },
      {
        get question() { return tr("How do I create a new experience listing?"); },
        get answer() { return tr("Use the Create flow in your dashboard to enter key details such as title, description, inclusions, meeting point, pricing, and availability."); },
      },
      {
        get question() { return tr("What are Voocly's content quality guidelines?"); },
        get answer() { return tr("Listings must be accurate, honest, and easy to understand, with clear itineraries, high-quality photos, transparent pricing, and realistic expectations for travelers."); },
      },
      {
        get question() { return tr("Can I duplicate an existing listing?"); },
        get answer() { return tr("Yes, you can clone an existing experience and adjust only the elements that differ, such as language, timing, or meeting point."); },
      },
      {
        get question() { return tr("How do I manage availability and schedules?"); },
        get answer() { return tr("You can set regular schedules, seasonal calendars, and blackout dates in your dashboard."); },
      },
      {
        get question() { return tr("Can I temporarily deactivate an experience?"); },
        get answer() { return tr("You can unpublish an experience at any time so it stops receiving new bookings while remaining available for future reactivation."); },
      },
    ],
  },
  {
    id: "pricing-commission-promotions",
    get label() { return tr("Pricing, commission, and promotions"); },
    questions: [
      {
        get question() { return tr("How does Voocly's commission work?"); },
        get answer() { return tr("Voocly charges a percentage commission on the retail price of each completed booking, and you receive the net amount after commission and applicable fees."); },
      },
      {
        get question() { return tr("Who controls the final price shown to travelers?"); },
        get answer() { return tr("You set the base retail price; Voocly displays this price to customers, subject to local tax or currency rules."); },
      },
      {
        get question() { return tr("Can I charge extra fees on the day of the tour?"); },
        get answer() { return tr("Any mandatory fees must be clearly disclosed in the listing; undisclosed on-site charges are strictly not allowed."); },
      },
    ],
  },
  {
    id: "bookings-availability",
    get label() { return tr("Bookings and availability management"); },
    questions: [
      {
        get question() { return tr("How are bookings confirmed on Voocly?"); },
        get answer() { return tr("Experiences are instantly confirmed based on your product availability."); },
      },
      {
        get question() { return tr("Where can I see upcoming and past bookings?"); },
        get answer() { return tr("All bookings can be managed from the Bookings section of your dashboard, where you can filter by date, status, product, or booking reference number."); },
      },
      {
        get question() { return tr("How do I handle overbookings or operational issues?"); },
        get answer() { return tr("If you cannot operate a confirmed booking, you must immediately inform the customer and propose alternatives or request a cancellation through our partner portal."); },
      },
    ],
  },
  {
    id: "cancellations-refunds-no-shows",
    get label() { return tr("Cancellations, refunds, and no-shows"); },
    questions: [
      {
        get question() { return tr("What cancellation policies can I choose?"); },
        get answer() { return tr("Voocly offers a standardized 24-hour cancellation policy."); },
      },
      {
        get question() { return tr("Who handles customer refunds?"); },
        get answer() { return tr("Voocly processes eligible refunds according to the cancellation policy and then adjusts your payout and commission for the affected booking."); },
      },
      {
        get question() { return tr("Do I earn commission on cancelled bookings?"); },
        get answer() { return tr("No, there is no payout for fully refunded or invalid bookings; only completed or chargeable bookings generate commission and partner earnings."); },
      },
      {
        get question() { return tr("How does Voocly handle no-shows?"); },
        get answer() { return tr("If a traveler does not show up and your listing and policy have been respected, you may still receive payment; you must report no-shows in the dashboard within the specified time window."); },
      },
      {
        get question() { return tr("Can I cancel a booking as a partner?"); },
        get answer() { return tr("You should only cancel when absolutely necessary, such as for safety, severe weather, or force majeure, and must provide a clear explanation; frequent supplier-initiated cancellations can affect your visibility and performance standing."); },
      },
    ],
  },
  {
    id: "payments-invoices-payouts",
    get label() { return tr("Payments, invoices, and payout settings"); },
    questions: [
      {
        get question() { return tr("When do I get paid for completed bookings?"); },
        get answer() { return tr("Payouts are processed on a regular monthly schedule once bookings are completed and past the cancellation window, similar to major OTAs."); },
      },
      {
        get question() { return tr("What payout methods are available?"); },
        get answer() { return tr("You can typically choose between bank transfer and PayPal, depending on your country and currency."); },
      },
      {
        get question() { return tr("Is there a minimum payout threshold?"); },
        get answer() { return tr("Voocly may apply a minimum payout amount for bank transfers, while PayPal often has no or a lower threshold, aligning with common OTA practices."); },
      },
      {
        get question() { return tr("Where can I find my invoices and payout reports?"); },
        get answer() { return tr("Invoices, payment confirmations, and downloadable booking lists are available in the Finance section of your partner dashboard."); },
      },
      {
        get question() { return tr("What should I do if I think a payment is missing or incorrect?"); },
        get answer() { return tr("First compare the invoice and booking list with your own records, then contact Voocly support with booking references and invoice numbers for investigation."); },
      },
    ],
  },
  {
    id: "taxes-fees-legal",
    get label() { return tr("Taxes, fees, and legal requirements"); },
    questions: [
      {
        get question() { return tr("Who is responsible for local taxes on experiences?"); },
        get answer() { return tr("As the supplier of the service, you are responsible for calculating, reporting, and paying applicable local taxes unless specific local arrangements or marketplace rules state otherwise."); },
      },
      {
        get question() { return tr("Do I need liability insurance to list on Voocly?"); },
        get answer() { return tr("Yes, you must hold appropriate liability insurance for your activities and provide proof on request; this is standard practice among leading OTAs."); },
      },
      {
        get question() { return tr("How does Voocly handle service fees and bank charges?"); },
        get answer() { return tr("Any Voocly service fee is included in the commission structure, while intermediary bank or currency conversion fees may be deducted by your bank or payment provider."); },
      },
      {
        get question() { return tr("Where can I read the full partner terms and conditions?"); },
        get answer() { return tr("Your rights and obligations are defined in the Policies tab, which you should review and accept during signup."); },
      },
    ],
  },
  {
    id: "performance-reviews-rankings",
    get label() { return tr("Performance, reviews, and rankings"); },
    questions: [
      {
        get question() { return tr("How are traveler reviews collected?"); },
        get answer() { return tr("After an experience, Voocly invites travelers to leave a rating and review, which appears on your listing and shop once it passes basic content checks."); },
      },
      {
        get question() { return tr("What factors influence my ranking and visibility?"); },
        get answer() { return tr("Customer preference, your conversion rate, review scores, cancellation rate, content quality, and pricing competitiveness all contribute to where and how often your experiences appear in search results."); },
      },
      {
        get question() { return tr("Can poor performance affect my account?"); },
        get answer() { return tr("Consistently low ratings, high cancellation rates, safety issues, or policy violations may lead to reduced visibility, temporary suspension, or delisting of experiences."); },
      },
    ],
  },
  {
    id: "customer-communication-operations",
    get label() { return tr("Customer communication and on-the-day operations"); },
    questions: [
      {
        get question() { return tr("Can customers contact me directly?"); },
        get answer() { return tr("Yes, customers can message you through email with questions about logistics, accessibility, or special requests before and after booking."); },
      },
      {
        get question() { return tr("What if a traveler is late or cannot find the meeting point?"); },
        get answer() { return tr("You should define a clear waiting time and meeting instructions in the listing, and use the available tools or provided contact, where allowed, to help guests on the day."); },
      },
      {
        get question() { return tr("How should I handle incidents or emergencies?"); },
        get answer() { return tr("Prioritize guest safety, follow your local emergency procedures, document the situation, and inform Voocly support as soon as reasonably possible."); },
      },
    ],
  },
  {
    id: "compliance-trust-safety",
    get label() { return tr("Compliance, trust, and safety"); },
    questions: [
      {
        get question() { return tr("What safety standards must my experiences meet?"); },
        get answer() { return tr("You must comply with all local laws and safety regulations, use qualified guides where required, maintain safe equipment, and follow Voocly's safety guidelines."); },
      },
      {
        get question() { return tr("What are Voocly's rules on misleading or inappropriate content?"); },
        get answer() { return tr("Listings must not contain offensive, discriminatory, or misleading content, and must accurately represent what travelers will receive."); },
      },
      {
        get question() { return tr("Can my account be suspended or terminated?"); },
        get answer() { return tr("Voocly may suspend or close accounts that repeatedly violate policies, show serious safety issues, or engage in fraud or abusive behavior."); },
      },
      {
        get question() { return tr("How does Voocly protect traveler and partner data?"); },
        get answer() { return tr("Voocly uses secure systems and follows applicable data protection laws; you may only use traveler data for delivering the booked service and must not misuse it for unsolicited marketing."); },
      },
    ],
  },
];

export default function SupplierFaqPage() {
  usePartnerTranslation();
  const handleTopicChange = (event) => {
    const target = document.getElementById(event.target.value);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="min-w-0 flex-1 px-3 py-6 sm:px-6 sm:py-10 lg:px-10">
      <div className="mx-auto w-full max-w-[1180px]">
        <div className="mb-5 sm:mb-8">
          <h1 className="text-2xl font-bold leading-tight text-[#0F1115] sm:text-4xl">{tr("Frequently asked questions")}</h1>
        </div>

        <div className="mb-5 lg:hidden">
          <label
            htmlFor="faq-topic-select"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-[#8A8F98]"
          >{tr("Topics")}</label>
          <select
            id="faq-topic-select"
            onChange={handleTopicChange}
            className="h-11 w-full rounded-lg border border-[#D9DDE3] bg-white px-3 text-sm font-medium text-[#0F1115] outline-none focus:border-[#FF5757] focus:ring-2 focus:ring-[#FF5757]/15"
          >
            {FAQ_TOPICS.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid min-w-0 gap-5 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-6">
          <aside className="hidden lg:sticky lg:top-24 lg:block lg:self-start">
            <nav
              aria-label={tr("FAQ topics")}
              className="rounded-lg border border-[#E5E7EB] bg-white p-3 shadow-sm"
            >
              <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#8A8F98]">{tr("Topics")}</p>
              <div className="flex flex-col gap-1">
                {FAQ_TOPICS.map((topic) => (
                  <a
                    key={topic.id}
                    href={`#${topic.id}`}
                    className="rounded-md px-3 py-2 text-start text-sm font-medium leading-snug text-[#4B5563] transition-colors hover:bg-[#FFF3F3] hover:text-[#FF5757]"
                  >
                    {topic.label}
                  </a>
                ))}
              </div>
            </nav>
          </aside>

          <div className="min-w-0 space-y-4 sm:space-y-5">
            {FAQ_TOPICS.map((topic) => (
              <section
                key={topic.id}
                id={topic.id}
                className="min-w-0 scroll-mt-24 rounded-lg border border-[#E5E7EB] bg-white p-4 shadow-sm sm:scroll-mt-28 sm:p-6"
              >
                <h2 className="break-words text-lg font-semibold leading-snug text-[#0F1115] sm:text-xl">
                  {topic.label}
                </h2>
                <div className="mt-4 divide-y divide-[#ECEEF1]">
                  {topic.questions.map((item) => (
                    <details key={item.question} className="group py-4 first:pt-0 last:pb-0">
                      <summary className="flex cursor-pointer list-none items-start justify-between gap-3 text-start text-sm font-semibold leading-5 text-[#111827] sm:items-center">
                        <span className="min-w-0 break-words">{item.question}</span>
                        <ChevronDown
                          className="mt-0.5 h-4 w-4 shrink-0 text-[#8A8F98] transition-transform duration-200 group-open:rotate-180 group-open:text-[#FF5757] sm:mt-0"
                          strokeWidth={1.8}
                        />
                      </summary>
                      <p className="mt-3 max-w-3xl break-words text-sm leading-6 text-[#5b5f66]">
                        {item.answer}
                      </p>
                    </details>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
